import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ParkingClient, ParkingClientOption, ParkingClientTypeOption } from '../../../../../core/models/parking/parking-client.model';
import { ApiParkingClientService } from '../../../../../core/services/api/parking/api-parking-client.service';
import { MapperParkingClientService } from '../../../../../core/mappers/parking/mapper-parking-client.service';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-parking-client',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-parking-client.component.html',
  styleUrl: './modal-parking-client.component.css',
})
export class ModalParkingClientComponent implements OnInit {
  @Input() itemId = 0;
  @Input({ required: true }) siteId!: number;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingClient;
  clientTypeOptions: ParkingClientTypeOption[] = [];
  parentClientOptions: ParkingClientOption[] = [];
  isLoading = signal(false);
  isSaving = signal(false);

  readonly roleOptions: { value: '' | 'distributor' | 'tenant' | 'standalone'; label: string }[] = [
    { value: '', label: '— ไม่ระบุ —' },
    { value: 'distributor', label: 'ฝ่ายอาคาร' },
    { value: 'tenant', label: 'ผู้เช่า' },
    { value: 'standalone', label: 'อิสระ' },
  ];

  private readonly api = inject(ApiParkingClientService);
  private readonly mapper = inject(MapperParkingClientService);
  private readonly actionMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean {
    return this.itemId > 0;
  }

  ngOnInit(): void {
    this.formData = this.mapper.createEmpty(this.siteId);
    this.loadOptions();
    if (this.isEdit) {
      this.loadDetail();
    }
  }

  onClose(): void {
    if (this.isSaving()) return;
    this.close.emit();
  }

  onSave(): void {
    if (this.isSaving()) return;
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อบริษัท');
      return;
    }

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน');
      return;
    }

    const payload = this.mapper.mapToPayload(this.formData, userId, menuId);
    this.isSaving.set(true);

    const req = this.isEdit
      ? this.api.update(this.itemId, payload)
      : this.api.insert(payload);

    req.subscribe({
      next: (res) => {
        this.isSaving.set(false);
        const mapped = this.actionMapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.close.emit();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'บันทึกไม่สำเร็จ');
      },
    });
  }

  private loadOptions(): void {
    this.api.getClientTypeOptions(this.siteId).subscribe({
      next: (res) => { this.clientTypeOptions = this.mapper.mapClientTypeOptions(res); },
      error: () => { this.clientTypeOptions = []; },
    });

    this.api.getParentClientOptions(this.siteId, this.isEdit ? this.itemId : undefined).subscribe({
      next: (res) => { this.parentClientOptions = this.mapper.mapClientOptions(res); },
      error: () => { this.parentClientOptions = []; },
    });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getById(this.itemId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapFromDto(res as never);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.toast.error('โหลดข้อมูลบริษัทผู้เช่าไม่สำเร็จ');
        this.isLoading.set(false);
        this.close.emit();
      },
    });
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
